import {
  encryptVote,
  hashKeccak256,
  blindMessage,
  unblindSignature,
  generateAESKey,
  encryptAESKeyAndIV,
} from '../utils/crypto';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface VoteSelection {
  race_id: string;
  selected_options: string[];
}

export interface VoteData {
  election_id: string;
  votes: VoteSelection[];
  timestamp: number;
  voter_nonce: string;
}

export interface VoteReceipt {
  receiptCode: string;
  txHash: string;
  electionId: string;
  timestamp: string;
}

/**
 * Complete voting process with cryptographic security
 */
export class VotingService {
  /**
   * Submit a vote with full cryptographic process:
   * 1. Encrypt vote data (AES-256-GCM)
   * 2. Generate vote hash (Keccak256)
   * 3. Blind the hash
   * 4. Request blind signature from server
   * 5. Unblind the signature
   * 6. Submit encrypted vote with signature
   */
  static async submitVote(
    electionId: string,
    votes: VoteSelection[],
    accessToken: string
  ): Promise<VoteReceipt> {
    try {
      // Step 1: Prepare vote data
      const voteData: VoteData = {
        election_id: electionId,
        votes,
        timestamp: Date.now(),
        voter_nonce: crypto.randomUUID(),
      };

      // Step 2: Generate AES key and encrypt vote
      const aesKey = generateAESKey();
      const { encryptedData, iv } = encryptVote(voteData, aesKey);

      // Step 3: Generate hash of encrypted vote
      const voteHash = hashKeccak256(encryptedData);
      console.log('Vote hash:', voteHash);

      // Step 4: Get election public key
      const publicKeyResponse = await fetch(
        `${API_URL}/v1/voting/elections/${electionId}/public-key`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!publicKeyResponse.ok) {
        throw new Error('Failed to get election public key');
      }

      const publicKeyData = await publicKeyResponse.json();
      const publicKey = publicKeyData.data.publicKey;

      // Step 5: Encrypt AES key and IV with RSA public key
      console.log('Encrypting AES key and IV with RSA...');
      const { encryptedKey: encryptedAESKey, encryptedIV: encryptedAESIV } =
        await encryptAESKeyAndIV(aesKey, iv, publicKey);
      console.log('AES key and IV encrypted');

      // Step 6: Blind the vote hash
      const { blindedHash, blindingFactor } = blindMessage(voteHash, publicKey);
      console.log('Blinded hash created');

      // Step 7: Request blind signature
      const signatureResponse = await fetch(
        `${API_URL}/v1/voting/blind-signature`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            electionId,
            blindedVoteHash: blindedHash,
          }),
        }
      );

      if (!signatureResponse.ok) {
        const error = await signatureResponse.json();
        throw new Error(error.message || 'Failed to get blind signature');
      }

      const signatureData = await signatureResponse.json();
      const blindedSignature = signatureData.data.blindedSignature;
      console.log('Blind signature received');

      // Step 8: Unblind the signature
      const signature = unblindSignature(blindedSignature, blindingFactor);
      console.log('Signature unblinded');

      // Step 9: Submit vote with signature and encrypted AES keys
      const submitResponse = await fetch(`${API_URL}/v1/voting/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          electionId,
          voteHash: voteHash.replace('0x', ''), // Remove 0x prefix for backend
          signature,
          encryptedVoteData: encryptedData,
          encryptedAESKey,
          encryptedAESIV,
        }),
      });

      if (!submitResponse.ok) {
        const error = await submitResponse.json();
        throw new Error(error.message || 'Failed to submit vote');
      }

      const receiptData = await submitResponse.json();
      console.log('Vote submitted successfully');

      return receiptData.data;
    } catch (error) {
      console.error('Error in voting process:', error);
      throw error;
    }
  }

  /**
   * Check if voter has already voted in an election
   */
  static async hasVoted(
    electionId: string,
    accessToken: string
  ): Promise<boolean> {
    try {
      const response = await fetch(
        `${API_URL}/v1/voting/elections/${electionId}/has-voted`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.data.hasVoted;
    } catch (error) {
      console.error('Error checking vote status:', error);
      return false;
    }
  }

  /**
   * Verify a receipt code
   */
  static async verifyReceipt(receiptCode: string): Promise<any> {
    try {
      const response = await fetch(
        `${API_URL}/v1/voting/receipt/${receiptCode}`
      );

      if (!response.ok) {
        throw new Error('Receipt not found');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error verifying receipt:', error);
      throw error;
    }
  }
}
